import React, { useState, useEffect } from 'react';
import { projectsAPI } from '../services/api';
import ProjectFileUpload from './ProjectFileUpload';
import ProjectExpenses from './ProjectExpenses';
import ProjectNotes from './ProjectNotes';
import './ProjectDetail.css';

const ProjectDetail = ({ project, houseId, onClose, onEdit, onDelete }) => {
  const [projectDetail, setProjectDetail] = useState(project);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (project?.id) {
      loadProjectDetail();
    }
  }, [project?.id]);

  const loadProjectDetail = async () => {
    try {
      setLoading(true);
      const response = await projectsAPI.getById(project.id);
      // Ensure files, expenses, and notes arrays are initialized
      const projectData = {
        ...response.data,
        files: response.data.files || [],
        expenses: response.data.expenses || [],
        notes: response.data.notes || [],
      };
      setProjectDetail(projectData);
    } catch (error) {
      console.error('Error loading project detail:', error);
      setProjectDetail(project); // Fallback to initial project data
    } finally {
      setLoading(false);
    }
  };


  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusColor = (status) => {
    // Handle both string and enum values
    const statusStr = typeof status === 'string' ? status : (status?.value || status);
    const colors = {
      planned: 'var(--info)',
      in_progress: 'var(--primary)',
      on_hold: 'var(--warning)',
      completed: 'var(--success)',
      cancelled: 'var(--error)',
    };
    return colors[statusStr] || 'var(--text-secondary)';
  };

  const getPriorityColor = (priority) => {
    // Handle both string and enum values
    const priorityStr = typeof priority === 'string' ? priority : (priority?.value || priority);
    const colors = {
      low: 'var(--info)',
      medium: 'var(--primary)',
      high: 'var(--warning)',
      urgent: 'var(--error)',
    };
    return colors[priorityStr] || 'var(--text-secondary)';
  };

  const calculateBudgetProgress = (estimated, actual) => {
    if (!estimated || estimated === 0) return 0;
    return Math.min((actual / estimated) * 100, 100);
  };

  if (loading && !projectDetail) {
    return <div className="project-detail-loading">Loading project details...</div>;
  }

  if (!projectDetail) {
    return <div className="project-detail-error">Project not found</div>;
  }

  const budgetProgress = projectDetail.budget_estimated
    ? calculateBudgetProgress(projectDetail.budget_estimated, projectDetail.budget_actual || 0)
    : 0;

  const statusStr = typeof projectDetail.status === 'string' ? projectDetail.status : (projectDetail.status?.value || 'planned');
  const priorityStr = typeof projectDetail.priority === 'string' ? projectDetail.priority : (projectDetail.priority?.value || 'medium');
  const statusColor = getStatusColor(statusStr);
  const priorityColor = getPriorityColor(priorityStr);

  return (
    <div className="project-detail">
      <div className="project-detail-header">
        <div className="header-left">
          <button className="btn-back" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back
          </button>
          <div>
            <h1>{projectDetail.name}</h1>
            <div className="project-header-badges">
              <span
                className="status-badge"
                style={{ backgroundColor: statusColor }}
              >
                {statusStr.replace('_', ' ')}
              </span>
              <span
                className="priority-badge"
                style={{ backgroundColor: priorityColor }}
              >
                {priorityStr}
              </span>
              {projectDetail.category && (
                <span className="category-badge">{projectDetail.category}</span>
              )}
            </div>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn-edit" onClick={onEdit}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            Edit
          </button>
          <button className="btn-delete" onClick={onDelete}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
            Delete
          </button>
        </div>
      </div>

      <div className="project-detail-tabs">
        <button
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab-button ${activeTab === 'files' ? 'active' : ''}`}
          onClick={() => setActiveTab('files')}
        >
          Files ({projectDetail.files?.length || 0})
        </button>
        <button
          className={`tab-button ${activeTab === 'expenses' ? 'active' : ''}`}
          onClick={() => setActiveTab('expenses')}
        >
          Expenses ({projectDetail.expenses?.length || 0})
        </button>
        <button
          className={`tab-button ${activeTab === 'notes' ? 'active' : ''}`}
          onClick={() => setActiveTab('notes')}
        >
          Notes ({projectDetail.notes?.length || 0})
        </button>
      </div>

      <div className="project-detail-content">
        {activeTab === 'overview' && (
          <div className="overview-section">
            {projectDetail.description && (
              <div className="detail-section">
                <h3>Description</h3>
                <p className="description-text">{projectDetail.description}</p>
              </div>
            )}

            <div className="detail-section">
              <h3>Budget</h3>
              <div className="budget-overview">
                <div className="budget-row">
                  <span className="budget-label">Estimated:</span>
                  <span className="budget-value">{formatCurrency(projectDetail.budget_estimated)}</span>
                </div>
                <div className="budget-row">
                  <span className="budget-label">Actual Spent:</span>
                  <span className="budget-value spent">{formatCurrency(projectDetail.budget_actual)}</span>
                </div>
                {projectDetail.budget_estimated && projectDetail.budget_estimated > 0 && (
                  <>
                    <div className="budget-row">
                      <span className="budget-label">Remaining:</span>
                      <span className={`budget-value ${(projectDetail.budget_estimated - (projectDetail.budget_actual || 0)) < 0 ? 'negative' : 'positive'}`}>
                        {formatCurrency(projectDetail.budget_estimated - (projectDetail.budget_actual || 0))}
                      </span>
                    </div>
                    <div className="budget-progress-container">
                      <div className="budget-progress-bar">
                        <div
                          className="budget-progress-fill"
                          style={{
                            width: `${budgetProgress}%`,
                            backgroundColor: budgetProgress > 100 ? 'var(--error)' : budgetProgress > 80 ? 'var(--warning)' : 'var(--primary)',
                          }}
                        />
                      </div>
                      <div className="budget-progress-text">
                        {budgetProgress.toFixed(1)}% of budget used
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="detail-section">
              <h3>Dates</h3>
              <div className="dates-grid">
                {projectDetail.start_date && (
                  <div className="date-item">
                    <span className="date-label">Start Date:</span>
                    <span className="date-value">{formatDate(projectDetail.start_date)}</span>
                  </div>
                )}
                {projectDetail.target_date && (
                  <div className="date-item">
                    <span className="date-label">Target Date:</span>
                    <span className="date-value">{formatDate(projectDetail.target_date)}</span>
                  </div>
                )}
                {projectDetail.completion_date && (
                  <div className="date-item">
                    <span className="date-label">Completion Date:</span>
                    <span className="date-value">{formatDate(projectDetail.completion_date)}</span>
                  </div>
                )}
                {!projectDetail.start_date && !projectDetail.target_date && !projectDetail.completion_date && (
                  <p className="no-dates">No dates set for this project</p>
                )}
              </div>
            </div>

            <div className="detail-section">
              <h3>Quick Stats</h3>
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-label">Files</span>
                  <span className="stat-value">{projectDetail.files?.length || 0}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Expenses</span>
                  <span className="stat-value">{projectDetail.expenses?.length || 0}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Checklist Items</span>
                  <span className="stat-value">{projectDetail.notes?.length || 0}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Expenses</span>
                  <span className="stat-value">{formatCurrency(projectDetail.budget_actual)}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Created</span>
                  <span className="stat-value">{formatDate(projectDetail.created_at)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'files' && (
          <div className="files-section">
            <ProjectFileUpload projectId={projectDetail.id} onUpload={loadProjectDetail} />
          </div>
        )}

        {activeTab === 'expenses' && (
          <div className="expenses-section">
            <ProjectExpenses projectId={projectDetail.id} onUpdate={loadProjectDetail} />
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="notes-section">
            <ProjectNotes projectId={projectDetail.id} onUpdate={loadProjectDetail} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectDetail;
